import { Grid, Flex, Text } from '@radix-ui/themes';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { ConsoleContainer } from '~/features/project/components/Console/ConsoleContainer';
import { Header } from '~/features/project/components/Header';
import { Workspace } from '~/features/project/components/Workspace';
import { TbChevronUp, TbTerminal2 } from 'react-icons/tb';

import { toast } from '~/components/Toast';
import { workspaceStore } from '~/stores/workspace';
import { cn } from '~/utils/classNames';

import { useStore } from '@nanostores/react';
import { Loading } from '~/components/Loading';
import { AuthOverlay } from '~/features/auth/components/AuthOverlay';
import { useSession } from '~/features/auth/context/Session';
import Styles from '~/features/project/components/css/workspace.module.scss';
import { PromptTemplate } from '~/features/promptTemplate/PromptTemplate';
import { useCreateProject } from '~/hooks/useCreateProject';
import { useGetAvailableConnections } from '~/hooks/useGetAvailableConnections';
import { useOrganisation } from '~/hooks/useOrganisation';
import { useProject } from '~/hooks/useProject';
import { useProjectSystems } from '~/hooks/useProjectSystems';
import { defaultQueryClient } from '~/lib/tanstackQuery/DefaultQueryClient';
import { Project } from '~/services/versori-api/types';
import { reFormatFiles, reFormatResearchFiles } from '~/utils/formatFiles';
import { chatDB } from '~/utils/indexedDB';
import { researchStore } from '~/stores/research';

export const Route = createFileRoute('/integrations/$projectId')({
  component: RouteComponent,
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context }) => {
    // Helper function to check authentication
    const checkAuth = () => {
      if (!context.session || !context.organisation) {
        toast({
          title: 'User not logged in',
          type: 'error',
        });
        throw redirect({ to: '/' });
      }
      return context.organisation.id;
    };

    checkAuth();
  },
});

function RouteComponent() {
  const { session } = useSession();
  const { organisation } = useOrganisation();
  const params = Route.useParams();
  const activeDockTab = useStore(workspaceStore.activeDockTab);
  const isDockOpen = useStore(workspaceStore.isDockOpen);
  const workspaceProject = useStore(workspaceStore.project);
  const promptTemplate = useStore(workspaceStore.promptTemplate);
  const { createProject } = useCreateProject();
  const { project } = useProject();

  const setWorkspaceProject = (project: Project) => {
    workspaceStore.setProject(project);
    if (project.currentFiles?.files?.length > 0) {
      const researchFiles = project.currentFiles.files.filter(
        (file) => file.filename.startsWith('research/') || file.filename === 'solution-document.md'
      );
      const projectFiles = project.currentFiles.files.filter(
        (file) => !file.filename.startsWith('research/') && file.filename !== 'solution-document.md'
      );
      if (researchFiles.length > 0) {
        researchStore.addDocuments(reFormatResearchFiles(researchFiles));
      }
      workspaceStore.addWorkspaceFiles({
        files: reFormatFiles(projectFiles),
      });
    }
  };

  useGetAvailableConnections({
    projectId: params.projectId,
    envId: project?.environments?.[0]?.id || '',
  });

  const { data: connectionTemplates } = useProjectSystems({ project: project });

  useEffect(() => {
    if (connectionTemplates) {
      workspaceStore.setConnectionTemplates(connectionTemplates.items || []);
    }
  }, [connectionTemplates]);

  const configureNewProject = useCallback(
    async ({ name, id }: { name: string; id: string }) => {
      // Early exit if not logged in or no organisation
      if (!session || !organisation?.id) {
        return;
      }
      try {
        await createProject({
          params: {
            path: {
              organisation_id: organisation?.id,
            },
          },
          body: {
            name: name,
            id: id,
          },
        });
      } catch (error) {
        console.error(error);
        throw redirect({ to: '/projects' });
      } finally {
        await chatDB.deleteProject(id); // Clean up
      }
    },
    [createProject, organisation?.id, session]
  );

  useEffect(() => {
    const initProject = async () => {
      const indexedDBProject = await chatDB.getProject(params.projectId);
      if (indexedDBProject) {
        configureNewProject({
          name: indexedDBProject.projectName,
          id: params.projectId,
        });
      }
    };
    initProject();
  }, [configureNewProject, params.projectId]);

  useEffect(() => {
    if (project) {
      setWorkspaceProject(project);
      if (session) {
        defaultQueryClient.invalidateQueries({ queryKey: ['getProjects', organisation?.id] });
      }
    }
  }, [project, organisation?.id, session, configureNewProject]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        workspaceStore.setIsDockOpen(!isDockOpen);
      }
    };

    if (session) {
      window.addEventListener('keydown', handleEsc);
    }

    return () => {
      if (session) {
        window.removeEventListener('keydown', handleEsc);
      }
    };
  }, [activeDockTab, isDockOpen, session]);

  useEffect(() => {
    return () => {
      workspaceStore.reset();
      researchStore.reset();
    };
  }, []);

  const [panelWidth, setPanelWidth] = useState<number | undefined>();

  // const handleConsoleToggle = () => {
  //   if (!session) {
  //     toast({
  //       title: 'Login to view the Console',
  //       description: 'It‘s free to trial Versori AI, please log in or request an account.',
  //       type: 'info',
  //     });
  //     return;
  //   }
  //   workspaceStore.setIsDockOpen(!isDockOpen);
  // };

  if (!workspaceProject.id) {
    return <Loading />;
  }

  return (
    <Grid
      height="100vh"
      width="100%"
      style={{
        display: 'grid',
        gridTemplateAreas: `
          "header"
          "interface"
        `,
        gridTemplateRows: '5.5rem 1fr',
      }}
    >
      <Header isDockOpen={isDockOpen} panelWidth={panelWidth} />
      <div className={cn(Styles.ProjectPage, !!activeDockTab && Styles.ProjectPageWithDock)}>
        <Workspace onPanelResize={setPanelWidth} />

        <PromptTemplate
          open={promptTemplate}
          onOpenChange={() => workspaceStore.togglePromptTemplate(!promptTemplate)}
        />
        {/* <Flex
          className={Styles.ConsoleBar}
          align="center"
          justify="between"
          gap="2"
          role="button"
          onClick={handleConsoleToggle}
          style={{ cursor: 'pointer' }}
        >
          <Flex align="center" gap="0.5rem">
            <TbTerminal2 color="var(--gray-11)" fontSize="1.4rem" />
            <Text size="1" weight="medium">
              Console
            </Text>
          </Flex>
          <Flex align="center" justify="center">
            <TbChevronUp color="var(--gray-11)" />
          </Flex>
        </Flex> */}
        <ConsoleContainer />
      </div>
      <AuthOverlay />
    </Grid>
  );
}
